import { Box, FormControl, FormControlLabel, Switch } from '@mui/material';

import BaseOutlinedInput from '@/components/shared/Form/BaseOutlinedInput';
import ClearAdornment from '@/components/shared/Form/ClearAdornment';
import { StyledFormLabel } from '@/components/shared/Form/StyledOutlinedInput';
import { useTheme } from '@/theme/useTheme';

export interface FormValues {
  displayName: string;
  organizationCode: string;
  organizationClaim: string;
  logoUrl: string;
  isActive: boolean;
}

export interface FormErrors {
  displayName?: string;
  organizationCode?: string;
  organizationClaim?: string;
  logoUrl?: string;
}

interface OrganizationFormFieldsProps {
  values: FormValues;
  fieldErrors: FormErrors;
  onChange: (field: keyof Omit<FormValues, 'isActive'>) => (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClear: (field: keyof Omit<FormValues, 'isActive'>) => void;
  onToggleStatus: (checked: boolean) => void;
  idPrefix?: string;
}

const FIELD_WIDTH = '320px';

export function OrganizationFormFields({
  values,
  fieldErrors,
  onChange,
  onClear,
  onToggleStatus,
  idPrefix = 'org',
}: OrganizationFormFieldsProps) {
  const theme = useTheme();

  const clearAdornment = (field: keyof Omit<FormValues, 'isActive'>, value: string) =>
    value ? <ClearAdornment fieldName={field} onClear={() => onClear(field)} /> : undefined;

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: theme.hhglobal.spacing.spacing[7].valuePx,
          flexWrap: 'wrap',
        }}
      >
        <BaseOutlinedInput
          id={`${idPrefix}-display-name`}
          placeholder='Enter Client Display Name'
          inputLabel='CLIENT DISPLAY NAME'
          value={values.displayName}
          onChange={onChange('displayName')}
          errorMessage={fieldErrors.displayName ? fieldErrors.displayName : null}
          size='small'
          isRequired
          customWidth={FIELD_WIDTH}
          clearAdornment={clearAdornment('displayName', values.displayName)}
        />
        <BaseOutlinedInput
          id={`${idPrefix}-organization-code`}
          placeholder='Enter Organization Code'
          inputLabel='ORGANIZATION CODE'
          value={values.organizationCode}
          onChange={onChange('organizationCode')}
          errorMessage={fieldErrors.organizationCode ? fieldErrors.organizationCode : null}
          size='small'
          isRequired
          customWidth={FIELD_WIDTH}
          clearAdornment={clearAdornment('organizationCode', values.organizationCode)}
        />
        <BaseOutlinedInput
          id={`${idPrefix}-organization-claim`}
          placeholder='Enter Organization Claim'
          inputLabel='ORGANIZATION CLAIM'
          value={values.organizationClaim}
          onChange={onChange('organizationClaim')}
          errorMessage={fieldErrors.organizationClaim ? fieldErrors.organizationClaim : null}
          size='small'
          isRequired
          customWidth={FIELD_WIDTH}
          clearAdornment={clearAdornment('organizationClaim', values.organizationClaim)}
        />
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: theme.hhglobal.spacing.spacing[7].valuePx,
          alignItems: 'flex-start',
          flexWrap: 'wrap',
        }}
      >
        <BaseOutlinedInput
          id={`${idPrefix}-logo-url`}
          placeholder='Enter Logo URL'
          inputLabel='LOGO URL'
          value={values.logoUrl}
          onChange={onChange('logoUrl')}
          errorMessage={fieldErrors.logoUrl ? fieldErrors.logoUrl : null}
          size='small'
          isRequired
          customWidth={FIELD_WIDTH}
          clearAdornment={clearAdornment('logoUrl', values.logoUrl)}
        />
        <FormControl size='small'>
          <StyledFormLabel htmlFor={`${idPrefix}-client-status`}>CLIENT STATUS</StyledFormLabel>
          <FormControlLabel
            control={
              <Switch
                id={`${idPrefix}-client-status`}
                checked={values.isActive}
                onChange={(e) => onToggleStatus(e.target.checked)}
                color='default'
              />
            }
            label={values.isActive ? 'Active' : 'Inactive'}
          />
        </FormControl>
      </Box>
    </>
  );
}
